/*
  MOWE sign-up popup (sections/mowe-signup-popup.liquid)
  - Waits for the age gate, then opens after `data-delay` seconds
  - Closing remembers "dismissed" for `data-dismiss-days`; signing up remembers "subscribed" for good
    (localStorage, with a cookie fallback when storage is blocked)
  - Signing up is a normal form post, so Shopify's spam protection (hCaptcha) can add its token and,
    if it wants, show a challenge. Before the post the dialog turns non-modal, so a challenge that
    hCaptcha lays over the page stays clickable. Shopify then sends the visitor back to the page
    (?customer_posted=true#MoweSignupForm): the popup opens with the code, the URL is cleaned up and
    the page goes back to the top instead of jumping to the form's anchor in the footer.
  - Theme editor: opens only while the section is selected
*/
(function () {
  if (customElements.get('mowe-signup-popup')) return;

  var KEY = 'mowe_signup_popup_v1';
  var PENDING_KEY = 'mowe_signup_popup_pending';
  var DAY = 86400000;

  function readState() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (raw) {
        var data = JSON.parse(raw);
        if (data && data.until && Date.now() <= data.until) return data.state;
      }
    } catch (e) {
      // Storage blocked: fall through to the cookie
    }
    try {
      var match = document.cookie.match(new RegExp('(?:^|; )' + KEY + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }

  function writeState(state, days) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify({ state: state, until: Date.now() + days * DAY }));
      return;
    } catch (e) {
      // localStorage blocked: try a cookie instead
    }
    try {
      document.cookie =
        KEY + '=' + encodeURIComponent(state) + '; path=/; max-age=' + Math.round(days * 86400) + '; SameSite=Lax' +
        (window.location.protocol === 'https:' ? '; Secure' : '');
    } catch (e) {
      // Cookies blocked too: the popup may show again on the next visit
    }
  }

  // A sign-up from this popup is under way (set before the post, cleared on the way back).
  // It expires after 30 minutes so an abandoned captcha does not block the popup forever.
  function pending(action) {
    try {
      var store = window.sessionStorage;
      if (action === 'set') {
        store.setItem(PENDING_KEY, String(Date.now()));
        return true;
      }
      if (action === 'clear') {
        store.removeItem(PENDING_KEY);
        return false;
      }
      var since = parseInt(store.getItem(PENDING_KEY) || '0', 10);
      if (since && Date.now() - since < 30 * 60000) return true;
      store.removeItem(PENDING_KEY);
    } catch (e) {
      // sessionStorage blocked: no pending state
    }
    return false;
  }

  // Drop ?customer_posted=true and #MoweSignupForm, and undo the jump to the form's anchor in the footer
  function cleanUpAfterPost() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('customer_posted');
      url.hash = '';
      window.history.replaceState(window.history.state, '', url.pathname + url.search);
    } catch (e) {
      // Old browser: leave the URL as it is
    }
    var toTop = function () {
      window.scrollTo(0, 0);
    };
    toTop();
    if (document.readyState !== 'complete') window.addEventListener('load', toTop, { once: true });
  }

  class MoweSignupPopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

      this.sectionId = this.dataset.sectionId;
      this.delay = Math.max(0, parseFloat(this.dataset.delay || '3')) * 1000;
      this.dismissDays = Math.max(1, parseFloat(this.dataset.dismissDays || '30'));
      this.designMode = this.hasAttribute('data-design-mode');

      this.form = this.querySelector('form');
      this.input = this.querySelector('input[type="email"]');
      this.submitButton = this.querySelector('[data-popup-submit]');
      this.message = this.querySelector('[data-popup-message]');
      this.signup = this.querySelector('[data-popup-signup]');
      this.success = this.querySelector('[data-popup-success]');
      this.copyButton = this.querySelector('[data-popup-copy]');

      this.onSubmit = this.onSubmit.bind(this);
      this.onCancel = this.onCancel.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);

      this.form.addEventListener('submit', this.onSubmit);
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('click', this.onClick);
      // The browser can force-close the dialog (e.g. Escape pressed twice): keep the page unlocked
      this.dialog.addEventListener('close', function () {
        document.documentElement.classList.remove('mowe-popup-open');
      });

      if (this.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        return;
      }

      var signingUp = pending();

      // Back from our sign-up post: show the code right away
      var posted = this.success && this.success.hasAttribute('data-posted');
      if (signingUp && posted) {
        pending('clear');
        writeState('subscribed', 3650);
        cleanUpAfterPost();
        this.showSuccess();
        this.open();
        return;
      }

      // Back from our sign-up post with an error from Shopify (e.g. the address was refused): show it
      if (signingUp && this.message && this.message.hasAttribute('data-popup-error')) {
        pending('clear');
        cleanUpAfterPost();
        this.open();
        return;
      }

      // A sign-up is under way (for example on Shopify's captcha page): never pop up meanwhile
      if (signingUp) return;

      if (readState()) return;

      var start = this.schedule.bind(this);
      if (document.body.classList.contains('age-gate--locked')) {
        document.addEventListener('mowe:age-gate-closed', start, { once: true });
      } else {
        start();
      }
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      document.documentElement.classList.remove('mowe-popup-open');
    }

    schedule() {
      // Fetch the images during the delay so the popup opens complete
      this.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
      this.timer = window.setTimeout(this.open.bind(this), this.delay);
    }

    open() {
      if (this.dialog.open) return;
      // Never on top of the age gate
      if (document.body.classList.contains('age-gate--locked')) {
        document.addEventListener('mowe:age-gate-closed', this.open.bind(this), { once: true });
        return;
      }
      this.dialog.classList.remove('is-closing');
      this.dialog.showModal();
      document.documentElement.classList.add('mowe-popup-open');
    }

    close(remember) {
      if (!this.dialog.open) return;
      if (remember && !this.designMode) {
        if (this.subscribed) writeState('subscribed', 3650);
        else writeState('dismissed', this.dismissDays);
      }
      var dialog = this.dialog;
      var finish = function () {
        dialog.classList.remove('is-closing');
        if (dialog.open) dialog.close();
        document.documentElement.classList.remove('mowe-popup-open');
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        finish();
        return;
      }
      dialog.classList.add('is-closing');
      window.setTimeout(finish, 200);
    }

    // A visitor clicked a sign-up link (e.g. the header line): open now, whatever was remembered
    openFromTrigger() {
      window.clearTimeout(this.timer);
      if (readState() === 'subscribed') this.showSuccess();
      this.open();
    }

    onCancel(event) {
      // Escape
      event.preventDefault();
      this.close(true);
    }

    onClick(event) {
      var target = event.target;
      if (target.closest('[data-popup-close]')) {
        this.close(true);
        return;
      }
      if (this.copyButton && target.closest('[data-popup-copy]')) {
        this.copyCode();
        return;
      }
      // A click on the blue area around the blob (outside the photo) closes it
      if (target === this.dialog || target.classList.contains('mowe-popup__stage')) {
        this.close(true);
      }
    }

    onSectionSelect(event) {
      if (event.detail && event.detail.sectionId === this.sectionId) this.open();
    }

    onSectionDeselect(event) {
      if (event.detail && event.detail.sectionId === this.sectionId) this.close(false);
    }

    setMessage(text, isError) {
      if (!this.message) return;
      this.message.textContent = text || '';
      if (isError) this.message.setAttribute('data-popup-error', '');
      else this.message.removeAttribute('data-popup-error');
      if (this.input) this.input.setAttribute('aria-invalid', isError ? 'true' : 'false');
    }

    onSubmit(event) {
      var email = (this.input.value || '').trim();
      this.input.value = email;
      if (!email || !this.input.checkValidity()) {
        // Stop here, and keep Shopify's captcha script from handling an address we already know is wrong
        event.preventDefault();
        event.stopImmediatePropagation();
        this.setMessage('Please enter a valid email address.', true);
        this.input.focus();
        return;
      }

      // Valid: let the browser post the form. Shopify's spam protection listens for this submit,
      // adds its token and may show a challenge first; afterwards Shopify sends the visitor back.
      this.setMessage('', false);
      pending('set');
      if (this.submitButton) this.submitButton.setAttribute('aria-busy', 'true');

      // A modal dialog makes the rest of the page inert and sits above everything, so a challenge
      // that hCaptcha adds to the page could not be clicked. Keep showing the popup, but non-modal.
      var modal = false;
      try {
        modal = this.dialog.matches(':modal');
      } catch (e) {
        modal = false;
      }
      if (this.dialog.open && modal) {
        this.dialog.close();
        this.dialog.show();
        document.documentElement.classList.add('mowe-popup-open');
      }
    }

    showSuccess() {
      this.subscribed = true;
      if (this.signup) this.signup.hidden = true;
      if (this.success) {
        this.success.hidden = false;
        var focusTarget = this.copyButton || this.success.querySelector('[data-popup-close]');
        if (focusTarget && this.dialog.open) focusTarget.focus();
      }
    }

    copyCode() {
      var button = this.copyButton;
      var label = button.querySelector('[data-popup-copy-label]');
      var code = button.getAttribute('data-popup-copy');
      var done = function () {
        if (!label) return;
        label.textContent = button.getAttribute('data-copied-label') || code;
        window.setTimeout(function () {
          label.textContent = code;
        }, 1800);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done, function () {});
      }
    }
  }

  customElements.define('mowe-signup-popup', MoweSignupPopup);

  // Any element with data-mowe-signup-open opens the popup (the header sign-up line uses it)
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-mowe-signup-open]');
    if (!trigger) return;
    var popup = document.querySelector('mowe-signup-popup');
    if (!popup || !popup.dialog || typeof popup.openFromTrigger !== 'function') return;
    event.preventDefault();
    popup.openFromTrigger();
  });
})();
